/**
 * Formatting utilities for subscription form data.
 * Used when loading subscription or template data into the form.
 */

export const formatAggregation = (value: string): string => {
  return value.charAt(0).toUpperCase() + value.slice(1);
};

export const formatChangeType = (value: string): string => {
  const map: Record<string, string> = {
    relative_value: "Relative value",
    absolute_value: "Absolute value",
  };
  return map[value] || value;
};

export const formatComparison = (value: string): string => {
  const map: Record<string, string> = {
    changes_by: "changes by",
    exceeds: "Exceeds",
    below: "below",
    changes_by_percent: "changes by %",
  };
  return map[value] || value;
};

export const formatDateRange = (value: string): string => {
  const map: Record<string, string> = {
    previous_day: "Previous day",
    previous_week: "Previous week",
    previous_month: "Previous month",
    previous_year: "Previous year",
    previous_day_trailing_7_day_avg: "Trailing 7-day avg",
  };
  return map[value] || value;
};

interface ScheduleResult {
  value: string;
  displayValue: string;
}

interface ScheduleData {
  frequency: "daily" | "weekly" | "monthly";
  time: string;
  daysOfWeek?: string[];
  dayOfMonth?: number;
}

export const formatSchedule = (cron: string): ScheduleResult => {
  const parts = cron.split(" ");
  const [minute, hour, dayOfMonth, , dayOfWeek] = parts;

  let frequency: "daily" | "weekly" | "monthly" = "daily";
  let daysOfWeek: string[] = [];
  let dayOfMonthNum = 1;
  let unsupported = false;

  if (dayOfWeek !== "*") {
    frequency = "weekly";
    const dayMap: Record<string, string> = {
      "0": "Sun",
      "1": "Mon",
      "2": "Tue",
      "3": "Wed",
      "4": "Thu",
      "5": "Fri",
      "6": "Sat",
    };
    daysOfWeek = dayOfWeek.split(",").map(d => dayMap[d] || "Mon");
  } else if (dayOfMonth !== "*") {
    frequency = "monthly";
    // A non-numeric / out-of-range day-of-month (e.g. a legacy "L" cron) can't
    // be represented by the schedule editor. Flag it as unsupported so the
    // schedule renders as the raw cron rather than a misleading "Monthly on
    // day 1" — consistent with the list-view formatter and the invalid-time
    // fallback below.
    const parsed = parseInt(dayOfMonth, 10);
    if (isNaN(parsed) || parsed < 1 || parsed > 31) {
      unsupported = true;
    } else {
      dayOfMonthNum = parsed;
    }
  }

  const hourNum = parseInt(hour, 10);
  const minuteNum = parseInt(minute, 10);

  // Handle invalid, unsupported, or wildcard values
  if (unsupported || isNaN(hourNum) || isNaN(minuteNum) || hour === "*" || minute === "*") {
    return {
      value: JSON.stringify({ frequency: "daily", time: "12:00 AM" }),
      displayValue: cron,
    };
  }

  const period = hourNum >= 12 ? "PM" : "AM";
  const hour12 = hourNum === 0 ? 12 : hourNum > 12 ? hourNum - 12 : hourNum;
  const time = `${hour12}:${minuteNum.toString().padStart(2, "0")} ${period}`;

  const scheduleData: ScheduleData = { frequency, time };
  if (frequency === "weekly") scheduleData.daysOfWeek = daysOfWeek;
  if (frequency === "monthly") scheduleData.dayOfMonth = dayOfMonthNum;

  let displayValue = "";
  if (frequency === "daily") {
    displayValue = `Daily at ${time}`;
  } else if (frequency === "weekly") {
    const daysList = daysOfWeek.join(", ");
    displayValue = `Weekly on ${daysList} at ${time}`;
  } else if (frequency === "monthly") {
    displayValue = `Monthly on day ${dayOfMonthNum} at ${time}`;
  }

  return {
    value: JSON.stringify(scheduleData),
    displayValue,
  };
};
